// ─────────────────────────────────────────────────────────────────────────────
// "IS THIS YOUR FIRM?" — THE QUESTION THAT REPLACED A SILENT MERGE.
//
// 🚨 WHAT THIS PREVENTS. The account resolver used to attach a partner to any
// account whose normalised company name matched, or merely CONTAINED, theirs.
// Account 11 "JLA" ended up holding two Sales Agents sponsored by different
// brokers that way — one ACH set standing in for two firms. So a name match is
// now a proposal a human answers, and this is the human.
//
// ── 🚨 A NAME MATCH IS A NAME MATCH ON BOTH PATHS ────────────────────────────
//
// ⚠️ THIS SCREEN MUST NEVER PRESENT ONE AS VERIFIED, FOR A REALTOR EITHER. What
// TREC verifies is a LICENSE and a SPONSORING BROKER. It says nothing about
// whether the firm someone typed is the firm they meant — and by the time this
// prompt appears, the sponsor number has ALREADY failed to resolve it, because a
// sponsor match attaches outright and never reaches this component. So every
// proposal shown here rests on the name alone, whoever is looking at it.
//
// 🔑 THE ASYMMETRY IS ABOUT WHAT CAN HAPPEN NEXT, NOT ABOUT THIS ANSWER.
//   realtor    — the firm is VERIFIABLE. The state license record names a
//                sponsoring broker, so Doorly can check this later and will.
//   originator — there is NO equivalent public record for mortgage companies.
//                NMLS Consumer Access forbids the bulk use that would be needed,
//                and the licensed routes are procurement. It is their word,
//                permanently, and the copy says so.
//
// ⚠️ AND IT SAYS WHY, NOT JUST LESS. "Is this your firm?" asked more quietly on
// one path reads as Doorly being unsure of its own data. Naming the reason —
// there is a public license record for real estate firms and none for mortgage
// companies — turns a weaker experience into an honest one. Same reasoning as the
// Showami note in the data-flow inventory and the "we don't verify it
// independently" line on the §2.5 screen: say what is known and how it is known.
import React, { useState } from 'react';

// ⚠️ KEYED ON REGISTRY, NOT PARTNER TYPE. It is the existence of a public license
// record that differs, and that belongs to the registry. A homebuilder reaches
// this prompt with no registry at all and correctly gets the neutral wording
// rather than a claim about a record they are not on.
const WHY = {
  state_real_estate: {
    can: 'Real estate firms are on the state license record, so we can check this against '
       + 'your license once you add it. We still need you to confirm it now — the license '
       + 'record tells us who sponsors you, not which of these firms you meant.',
  },
  nmls: {
    can: 'We can check a realtor’s firm against the state license record. There is no '
       + 'equivalent public record for mortgage companies, so this one is your word — '
       + 'we’re asking because we can’t look it up, not because we doubt you.',
  },
  none: {
    can: 'We ask rather than assume. Two firms can share a name, and getting this wrong '
       + 'would put your payments on somebody else’s account.',
  },
};

function FirmMatchPrompt({ question = 'is_this_your_firm', companyName, candidates = [], registry = 'none', onJoin, onCreate, busy }) {
  const [choice, setChoice] = useState(null);
  const why = WHY[registry] || WHY.none;

  // 🚨 THE OTHER DIRECTION: they said their firm was already here and nothing
  // matched. Not an error and not a rejection — the profile is saved. Almost
  // always they typed the name differently from whoever registered first, and
  // the fix is a spelling they can get from a colleague, so the prompt says
  // exactly what was searched for rather than "not found".
  const notFound = question === 'expected_firm_not_found';
  // 🚨 A THIRD QUESTION, BECAUSE THE SECOND ONE'S COPY WOULD BE FALSE HERE. The
  // match branch below says "a firm already on DoorlyPro has the same name" —
  // true of an exact normalised match and a lie about a near one. A near match
  // is a spelling that is CLOSE, offered so one slipped keystroke does not found
  // a duplicate firm, and the partner has to be told which of the two claims is
  // being made or they cannot judge the answer.
  const didYouMean = question === 'did_you_mean';

  return (
    <div style={{ border: '2px solid #e5e7eb', borderRadius: 14, padding: 22, background: '#fff' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#111827' }}>
        {notFound ? 'We couldn’t find that firm' : didYouMean ? 'Did you mean this firm?' : 'Is this your firm?'}
      </h3>

      {/* ⚠️ THE CLAIM IS STATED AS A NAME MATCH, IN THOSE WORDS. "We found your
          firm" would assert something nothing here checked. */}
      {notFound ? (
        <p style={{ margin: '0 0 14px', fontSize: 14, color: '#4b5563' }}>
          You told us your broker is already on DoorlyPro, but nothing here is registered as{' '}
          <strong>{companyName}</strong>. Usually that means it was entered slightly differently —
          worth checking with a colleague what name they used. If it really isn’t here yet, we’ll
          set it up.
        </p>
      ) : didYouMean ? (
        <p style={{ margin: '0 0 14px', fontSize: 14, color: '#4b5563' }}>
          You entered <strong>{companyName}</strong>. That isn’t an exact match for anything here,
          but it’s spelled almost the same as a firm already on DoorlyPro. If one of these is
          yours, joining it keeps your team on one account — otherwise we’ll set yours up as new.
        </p>
      ) : (
        <p style={{ margin: '0 0 14px', fontSize: 14, color: '#4b5563' }}>
          You entered <strong>{companyName}</strong>. A firm already on DoorlyPro has
          the same name{candidates.length > 1 ? 's' : ''}. That is all we know so far — we
          matched the name, nothing else.
        </p>
      )}

      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        {candidates.map((c) => (
          <button
            key={c.id} type="button" disabled={busy}
            onClick={() => { setChoice(c.id); onJoin(c.id); }}
            style={{
              textAlign: 'left', padding: '14px 16px', borderRadius: 10, cursor: busy ? 'default' : 'pointer',
              border: `2px solid ${choice === c.id ? '#3a6ad9' : '#e5e7eb'}`, background: '#fff',
            }}
          >
            <div style={{ fontWeight: 600, color: '#111827' }}>Yes — join {c.companyName}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
              {c.partnerCount === 1 ? '1 person is' : `${c.partnerCount} people are`} already on this firm’s account.
            </div>
          </button>
        ))}

        <button
          type="button" disabled={busy}
          onClick={() => { setChoice('new'); onCreate(); }}
          style={{
            textAlign: 'left', padding: '14px 16px', borderRadius: 10, cursor: busy ? 'default' : 'pointer',
            border: `2px solid ${choice === 'new' ? '#3a6ad9' : '#e5e7eb'}`, background: '#fff',
          }}
        >
          <div style={{ fontWeight: 600, color: '#111827' }}>
            {notFound ? 'Set it up — we’re the first here' : 'No — mine is a different firm'}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
            {notFound
              ? 'Your colleagues will be offered this firm when they register.'
              : 'We’ll set up a separate account under the same name. We won’t ask you again.'}
          </div>
        </button>
      </div>

      <p style={{ margin: 0, fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>{why.can}</p>
    </div>
  );
}

export default FirmMatchPrompt;
